
import BookingSdnBanner from "@/app/ui/components/booking-sdn-banner/index.vue";
/* eslint-disable @typescript-eslint/camelcase */
import { Vue, Options, prop } from "vue-class-component";
import { createFocusTrap } from "focus-trap";
import {
  AddressTypeV4,
  ChargeableWeightBreakdownV4,
  CommodityBookingV4,
  CommodityCategoryV4,
  CommodityCiplBookingV4,
  CreateBookingV4,
  ErrorBookingV4,
  PaymentMethodBookingV4,
  PaymentMethodOptionV4,
  PaymentMethodV4,
  PieceBookingV4,
  ProtectionPackingBookingV4,
  ProductBookingV4,
  ProductCardItem,
  ReceiverBookingV4,
  SenderBookingV4,
  SupportingDocBookingV4,
  DocInternationalBookingV4,
  AdditionalDocumentBookingV4,
  FormCompletionPanelItemV4,
  FormCompletionSectionBookingV4,
  FormCompletionSectionKeyV4,
  FormCompletionFieldRowV4,
  FormCompletionSectionStateV4
} from "@/domain/entities/BookingV4";
import { Commodity } from "@/domain/entities/Commodity";
import { BookingV4Controller } from "@/app/ui/controllers/BookingV4Controller";
import { CIPLTypeV4, GetEstimationPriceV4, RequestListCommodityV4 } from "@/data/payload/api/BookingV4Request";
import { BookingController } from "@/app/ui/controllers/BookingController";
// import { WoodpackingEligibilityQuery } from "@/data/payload/api/BookingRequest";
import { ProductConfigurationRule } from "@/domain/entities/Product";
import { AccountController } from "@/app/ui/controllers/AccountController";
import SenderSectionV4 from "./components/detail-sender/index.vue";
import ReceiverSectionV4 from "./components/detail-receiver/index.vue";
import { DistrictData, ZipCodeEntities } from "@/domain/entities/Location";
import {
  getCurrencyByCountryCode,
  isMalaysiaDistrict
} from "@/app/infrastructures/misc/common-library/MalaysiaDistrictFieldRules";
import {
  ConfigValidationForm,
  PosterProduct
} from "@/domain/entities/CreateBookingV3";
import {
  ComodityProductPoster,
  PayloadPosterProduct
} from "@/domain/entities/Booking";
import { countries as COUNTRY_OPTIONS } from "@/app/infrastructures/misc/BulkData";
import { LocationController } from "@/app/ui/controllers/LocationController";
import {
  ShipmentBookingDetail,
  SttAdjustment,
  SttAdjustmentRule,
  SttReferenceAdjustment
} from "@/domain/entities/ShipmentBooking";
import { FlagsFeatureShipmentC1 } from "@/feature-flags/flags-shipment-c1";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { FlagsPermissionBookingV3 } from "@/feature-flags/flag-booking-v3";
import { FlagsPermissionBookingV4 } from "@/feature-flags/flag-booking-v4";
import { CustomProcessEditAddressForm } from "@/domain/entities/CustomProcess";
import { codePhoneCountry } from "@/app/ui/views/shipment/booking/booking-utils";
import { ErrorMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import { CLIENT_ERROR, formatByCountry, formatInputByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import GoodsPackageV4 from "./components/goods-package/index.vue";
import ProductCardListV4 from "./components/jenis-pengiriman/product-card-list.vue";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";
import { parseCiplNumber } from "./utils";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { Pagination } from "@/domain/entities/Pagination";
import { debounce } from "lodash";
import { PartnerController } from "@/app/ui/controllers/PartnerController";
import { ClientPartnerOptions } from "@/domain/entities/CnManifest";
import { PartnerDetail } from "@/domain/entities/Partner";
import SupportingDocSectionV4 from "./components/supporting-documents/index.vue";
import BookingStickyFooterV4 from "./components/footer-v2/index.vue";
import TotalTarifSectionV4 from "./components/footer-v2/components/check-tariff.vue";
import PaymentMethodSelectorV4 from "./components/footer-v2/payment/index.vue";
import FormCompletionPanel from "./components/form-completion/index.vue";
import { getRoundedChargeableWeightByRules } from "@/app/infrastructures/misc/common-library/getRoundedChargeableWeightByRules";
import { isEligibleForMaxVolumeWeightDiscount } from "@/app/infrastructures/misc/common-library/IsEligibleForMaxVolumeWeightDiscount";
import { EstimationPrice } from "@/domain/entities/Booking";
import ProtectionPackingSummary from "./components/protection-packing-summary/index.vue";
import { validationCurrentIndex } from "../create-booking-v3/utils";

type SupportingDocCompletionFieldKey =
  | "email"
  | "idNumber"
  | "taxNumber"
  | "imageId"
  | "imageTax"
  | "imageBeforePacking"
  | "imageAfterPacking";

type FormCompletionSectionKeyV4Value = typeof FormCompletionSectionKeyV4[keyof typeof FormCompletionSectionKeyV4];
const FORM_COMPLETION_SECTION_ANCHORS: Record<
  FormCompletionSectionKeyV4Value,
  string
> = {
  sender: "booking-v4-section-sender",
  receiver: "booking-v4-section-receiver",
  reference_number: "booking-v4-section-reference-number",
  goods_pieces: "booking-v4-section-goods-pieces",
  product_type: "booking-v4-section-product-type",
  supporting_documents: "booking-v4-section-supporting-documents",
  booking_v4_section_payment_method: "booking-v4-section-payment-method"
};
// Display order for the Dokumen Pendukung rincian rows — aligned to the
// shipped SupportingDocFieldKey (GENESIS-19572), not a separate naming.
const SUPPORTING_DOC_COMPLETION_FIELD_ORDER: SupportingDocCompletionFieldKey[] = [
  "email",
  "idNumber",
  "taxNumber",
  "imageId",
  "imageTax",
  "imageBeforePacking",
  "imageAfterPacking"
];

const SUPPORTING_DOC_FIELD_LABEL_I18N: Record<
  typeof SUPPORTING_DOC_COMPLETION_FIELD_ORDER[number],
  string
> = {
  email: "bookingV4.formCompletion.email",
  idNumber: "bookingV4.formCompletion.idNumber",
  taxNumber: "bookingV4.formCompletion.taxNumber",
  imageId: "bookingV4.formCompletion.upload.idCard",
  imageTax: "bookingV4.formCompletion.upload.taxCard",
  imageBeforePacking: "bookingV4.formCompletion.upload.packing",
  imageAfterPacking: "bookingV4.formCompletion.upload.packing"
};
import { applyRecommendationOrder } from "../create-booking-v3/utils/applyRecommendationOrder";

class Props {
  modelValue = prop<boolean>({
    default: false,
    type: Boolean,
    required: true
  });
  form = prop<CustomProcessEditAddressForm>({
    default: () => new CustomProcessEditAddressForm(),
    type: Object
  });
  posIDForInternal = prop<string>({
    default: "",
    type: String
  });
  posNameForInternal = prop<string>({
    default: "",
    type: String
  });
  rtsOrCnx = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:modelValue", "onSubmit"],
  components: {
    BookingSdnBanner,
    SenderSectionV4,
    ReceiverSectionV4,
    GoodsPackageV4,
    SupportingDocSectionV4,
    BookingStickyFooterV4,
    TotalTarifSectionV4,
    PaymentMethodSelectorV4,
    ProtectionPackingSummary,
    ProductCardListV4,
    FormCompletionPanel
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.init();
        }
      }
    },
    estimationPriceContextPayload: {
      handler() {
        this.getEstimationPriceDebounced();
      },
      deep: true,
      immediate: true
    },
    productUpsellingContextSignature: {
      handler() {
        this.fetchProductUpsellingDebounced();
      },
      deep: true
    }
  }
})
class ModalBookingReverseJourneyV4 extends Vue.with(Props) {
  declare $refs: {
    receiverDetail: any
    "modal-booking-reverse-v4": any
  }

  isLoadingPartners = false;
  partnersData: ClientPartnerOptions[] = [];
  selectedPartnerPos: any = "";
  disableSelectPartnerField = false;
  isLoadingPartnerField = false;
  isFtz = false;
  isCipl = false;

  supportingDocFieldRules: Partial<
    Record<SupportingDocCompletionFieldKey, SttAdjustmentRule>
  > = {};
  hasAttemptedSubmit = false;
  estimationLoading = false;
  estimationIsError = false;
  estimationRequestId = 0;

  productUpsellingLoading = false;
  productUpsellingIsError = false;
  fetchPromisesAfterGetUpselling: string[] = [];

  get isForeignAccount(): boolean {
    return AccountController.accountData.accountIsForeign;
  }

  get parentId() {
    return AccountController.accountData.account_type_detail.id;
  }

  get isUserHasPartnerPos() {
    return !!AccountController.accountData.account_type_detail
      .partnerIdSttReturn;
  }

  get visible(): boolean {
    return this.modelValue;
  }

  set visible(val: boolean) {
    this.$emit("update:modelValue", val);
  }

  get showItemDetails(): boolean {
    return this.isCipl || this.isFtz;
  }

  get isClient() {
    return this.form.sttBookedForType === "client";
  }

  get isInternationalOrigin(): boolean {
    return (
      !!this.form.sttOriginCountryCode &&
      !!this.form.sttDestinationCountryCode &&
      this.form.sttOriginCountryCode !== this.form.sttDestinationCountryCode
    );
  }

  get isOriginCod(): boolean {
    return (
      this.form.sttIsCod?.toLowerCase() === "yes" || !!this.sttDetail?.sttIsCod
    );
  }

  get isMaskedCodInternationalClient(): boolean {
    return (
      this.isClient &&
      this.isInternationalOrigin &&
      this.isOriginCod &&
      (this.isRts || this.isReroute)
    );
  }

  get isShowClientTariff() {
    return (
      ShipmentBookingController.shipmentBookingDetail?.sttClient
        ?.clientShowShippingTariff || false
    );
  }

  get isTariffMaksed(): boolean {
    return (
      (this.isClient && !this.isShowClientTariff) ||
      this.isMaskedCodInternationalClient
    );
  }

  get isTariffContextReady(): boolean {
    const hasCompletePieces =
      this.bookingFormV4.pieces.length > 0 &&
      this.bookingFormV4.pieces.every(
        (piece) =>
          piece.grossWeight > 0 &&
          piece.length > 0 &&
          piece.width > 0 &&
          piece.height > 0
      );
    return !!(
      this.districtDetail.code &&
      this.destinationCityDetail?.code &&
      this.bookingFormV4.commodity.id &&
      this.bookingFormV4.product.productType &&
      hasCompletePieces
    );
  }

  get estimationPricePayload(): GetEstimationPriceV4 {
    return new GetEstimationPriceV4({
      ...this.estimationPriceContextPayload
    });
  }

  get estimationPriceContextPayload(): GetEstimationPriceV4 {
    const protectionPacking = this.bookingFormV4.protectionPacking;
    const paymentMethod = this.bookingFormV4.paymentMethod;
    const goodsPrice = protectionPacking.itemPrice || 0;
    const isCodOrDfod = [
      PaymentMethodV4.COD.toString(),
      PaymentMethodV4.DFOD.toString()
    ].includes(paymentMethod.method);
    return new GetEstimationPriceV4({
      originId: this.districtDetail.code,
      destinationId: this.destinationCityDetail?.code || "",
      commodityId: this.bookingFormV4.commodity.id,
      productType: this.bookingFormV4.product.productType,
      accountType: "pos",
      accountRefId: 0,
      goodsPrice,
      insuranceType: protectionPacking.insuranceType,
      isWoodpacking: protectionPacking.woodpacking,
      isHaveTaxId:
        this.isFtz && !!this.bookingFormV4.supportingDoc.ftzForm.taxNumber,
      pieces: this.bookingFormV4.pieces.map((piece) => ({
        pieceLength: piece.length,
        pieceWidth: piece.width,
        pieceHeight: piece.height,
        pieceGrossWeight: piece.grossWeight
      })) as any,
      // Limit Tagihan has no dedicated payload field — it doesn't affect
      // tariff calculation (Jira note on this ticket's Technical Steps §2).
      isCod: false,
      isDfod: false,
      codHandling: "",
      codAmount: goodsPrice || 0,
      isDisablePromo: true,
      promoAppliedTo: "retail",
      isPad: false,
      shipmentPrefix: "",
      hideError: false
    });
  }

  get formCompletionItems(): FormCompletionPanelItemV4[] {
    const items: FormCompletionPanelItemV4[] = [
      new FormCompletionSectionBookingV4({
        key: FormCompletionSectionKeyV4.SENDER,
        label: this.$t("bookingV4.formCompletion.sender") as string,
        state: this.resolveSectionCompletionState(
          FormCompletionSectionKeyV4.SENDER,
          this.isSenderSectionValid
        )
      }),
      new FormCompletionSectionBookingV4({
        key: FormCompletionSectionKeyV4.RECEIVER,
        label: this.$t("bookingV4.formCompletion.recipient") as string,
        state: this.resolveSectionCompletionState(
          FormCompletionSectionKeyV4.RECEIVER,
          this.isReceiverSectionValid
        )
      })
    ];

    items.push(
      new FormCompletionSectionBookingV4({
        key: FormCompletionSectionKeyV4.GOODS_PIECES,
        label: this.$t("bookingV4.formCompletion.goods") as string,
        state: this.resolveSectionCompletionState(
          FormCompletionSectionKeyV4.GOODS_PIECES,
          this.isGoodsPiecesSectionValid
        )
      })
    );

    if (this.showItemDetails) {
      const supportingDocumentsItem: FormCompletionPanelItemV4 = new FormCompletionSectionBookingV4(
        {
          key: FormCompletionSectionKeyV4.SUPPORTING_DOCUMENTS,
          label: this.$t("bookingV4.formCompletion.doc") as string,
          state: this.resolveSectionCompletionState(
            FormCompletionSectionKeyV4.SUPPORTING_DOCUMENTS,
            this.isSupportingDocSectionValid
          )
        }
      );
      supportingDocumentsItem.fields = this.supportingDocCompletionFields;
      items.push(supportingDocumentsItem);
    }

    return items;
  }

  get isMaxTotalGrossweight() {
    return (
      this.bookingFormV4.pieces.reduce((prev, curr) => {
        return prev + curr.grossWeight;
      }, 0) > 400
    );
  }

  get isGoodsPiecesSectionValid(): boolean {
    return (
      this.isCommoditySectionValid
    );
  }

  resolveSectionCompletionState(
    sectionKey: FormCompletionSectionKeyV4Value,
    isValid: boolean
  ): string {
    if (isValid) return FormCompletionSectionStateV4.COMPLETE;
    return this.shouldShowSectionValidationError(sectionKey)
      ? "error"
      : FormCompletionSectionStateV4.INCOMPLETE;
  }

  get supportingDocCompletionFields(): FormCompletionFieldRowV4[] {
    return SUPPORTING_DOC_COMPLETION_FIELD_ORDER.filter(
      (fieldKey) =>
        !!this.supportingDocFieldRules[fieldKey] &&
        this.supportingDocFieldRules?.[fieldKey]?.isRequired
    ).map((fieldKey) => {
      const rule = this.supportingDocFieldRules[fieldKey] as SttAdjustmentRule;
      return new FormCompletionFieldRowV4({
        fieldKey,
        fieldLabel: this.$t(
          SUPPORTING_DOC_FIELD_LABEL_I18N[fieldKey]
        ) as string,
        status: rule.isRequired ? "wajib" : "opsional"
      });
    });
  }

  shouldShowSectionValidationError(
    sectionKey: FormCompletionSectionKeyV4Value
  ): boolean {
    if (sectionKey === FormCompletionSectionKeyV4.SUPPORTING_DOCUMENTS) {
      const hasEnteredDocsSection =
        this.currentIndex !== -1 && this.currentIndex >= 20;
      return (
        this.showItemDetails &&
        !this.isSupportingDocSectionValid &&
        (this.hasAttemptedSubmit || hasEnteredDocsSection)
      );
    }

    const sectionMinIndex: Partial<Record<
      FormCompletionSectionKeyV4Value,
      number
    >> = {
      [FormCompletionSectionKeyV4.SENDER]: 1,
      [FormCompletionSectionKeyV4.RECEIVER]: 5,
      [FormCompletionSectionKeyV4.GOODS_PIECES]: 11
    };

    const minIndex = sectionMinIndex[sectionKey];
    const isBypassed =
      minIndex !== undefined &&
      this.currentIndex !== -1 &&
      (this.currentIndex === 11 || this.currentIndex > minIndex + 3);

    const shouldEvaluate = this.hasAttemptedSubmit || isBypassed;
    if (!shouldEvaluate) return false;

    switch (sectionKey) {
      case FormCompletionSectionKeyV4.SENDER:
        return !this.isSenderSectionValid;
      case FormCompletionSectionKeyV4.RECEIVER:
        return !this.isReceiverSectionValid;
      case FormCompletionSectionKeyV4.GOODS_PIECES:
        return !this.isGoodsPiecesSectionValid;
      // case FormCompletionSectionKeyV4.SUPPORTING_DOCUMENTS:
      //   return (
      //     this.showItemDetails && !this.isSupportingDocSectionValid
      //   );
      case FormCompletionSectionKeyV4.PRODUCT_TYPE:
        return !this.bookingFormV4.product.productType;
      default:
        return false;
    }
  }

  onFormCompletionNavigate(sectionKey: FormCompletionSectionKeyV4Value) {
    const anchorId = FORM_COMPLETION_SECTION_ANCHORS[sectionKey];
    document.getElementById(anchorId)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest"
    });
  }

  closeModal() {
    this.visible = false;
  }

  focusTrap: any = null;

  mounted() {
    const modal = (this.$refs["modal-booking-reverse-v4"] as any)?.$el;
    // if (modal) {
    //   this.focusTrap = createFocusTrap(modal as HTMLElement, {
    //     allowOutsideClick: true,
    //     clickOutsideDeactivates: true,
    //     onDeactivate: () => {
    //       this.closeModal();
    //     }
    //   });
    //   this.focusTrap.activate();
    // }
  }

  sdnReady = false;

  async init() {
    this.sdnReady = false;
    this.setBookingFormV4();
    this.fetchCiplCommodityOptions();
    this.refreshCommodityRecommendations();
    this.fetchCommodityCategories();
    this.fetchProductUpselling();
    this.supportingDocFieldRulesReady = true;
  }

  unmounted() {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }

  get districtDetail(): DistrictData {
    return LocationController.districtDetail;
  }

  get sttDetail(): ShipmentBookingDetail {
    return ShipmentBookingController.shipmentBookingDetail;
  }

  bookingFormV4 = new CreateBookingV4();
  setBookingFormV4() {
    const form = this.form;
    const detail = this.sttDetail;
    const supportingDoc = this.mapSupportingDoc(this.form);

    this.bookingFormV4 = new CreateBookingV4({
      sender: new SenderBookingV4({
        name: form.senderName,
        phoneFlag: form.sttSenderPhoneCode
          ? codePhoneCountry(form.sttSenderPhoneCode).flag
          : "",
        phoneCode: form.sttSenderPhoneCode,
        phone: form.senderPhone,
        address: form.senderAddress,
        originCity: form.originCity || "",
        originCountryCode: ""
      }),
      receiver: new ReceiverBookingV4({
        name: form.consigneeName,
        phoneFlag: form.sttRecipientPhoneCode
          ? codePhoneCountry(form.sttRecipientPhoneCode).flag
          : "",
        phoneCode: form.sttRecipientPhoneCode,
        phone: form.consigneePhone,
        addressType: new OptionsClass({
          value: form.addressType || AddressTypeV4.HOME
        }),
        address: form.consigneeAddress,
        destinationCity: form.destinationCity || "",
        postalCode: new DistrictData({
          name: form.postalCode || ""
        }),
        destinationCountryCode: form.destinationCity?.countryCode || ""
      }),
      commodity: new CommodityBookingV4({
        id: Number(form.commodity.commodity_id),
        name: this.isForeignAccount
          ? form.commodity.commodity_name_en || form.commodity.commodity_name
          : form.commodity.commodity_name || "",
        code: form.commodity.commodity_code || "",
        isQuarantine: form.commodity.isQuarantine,
        isDangerousGoods: form.commodity.isDangerousGoods
      }),
      paymentMethod: new PaymentMethodBookingV4({
        codAmount: this.originCodAmount,
        method: this.reverseJourneyOriginPaymentMethod
      }),
      product: new ProductBookingV4({
        productType: form.product || ""
      }),
      supportingDoc,
      pieces: form.sttPieces.map((piece) => {
        return new PieceBookingV4({
          grossWeight: piece.grossWeight,
          height: piece.dimension.height,
          length: piece.dimension.length,
          width: piece.dimension.width,
          volumeWeight: piece.volumeWeight
        })
      }),
    });

    // Proteksi, Harga Barang & Packing Kayu — override retail defaults:
    // proteksi & packing kayu start unchecked, Harga Barang prefilled from the
    // origin STT (GENESIS-20217).
    this.bookingFormV4.protectionPacking = new ProtectionPackingBookingV4({
      protectionChecked: this.form.insurance.includes("basic"),
      insuranceType: this.form.insurance,
      itemPrice: this.originGoodsPrice,
      woodpacking: false,
    });
    const piecesData = detail.SttPieces?.length
      ? detail.SttPieces
      : detail.sttPieces;
    if (Array.isArray(piecesData) && piecesData.length > 0) {
      this.bookingFormV4.pieces = piecesData.map(
        (piece: any) =>
          new PieceBookingV4({
            length: Number(
              piece.dimension?.length || piece.stt_piece_length || 0
            ),
            width: Number(piece.dimension?.width || piece.stt_piece_width || 0),
            height: Number(
              piece.dimension?.height || piece.stt_piece_height || 0
            ),
            grossWeight: Number(
              piece.grossWeight || piece.stt_piece_gross_weight || 0
            ),
            volumeWeight: Number(
              piece.volumeWeight || piece.stt_piece_volume_weight || 0
            )
          })
      );
    }

    if (detail.postalCodeDestination) {
      LocationController.setZipCodeData(
        new ZipCodeEntities(new Pagination(1, 10), [
          new DistrictData({ name: detail.postalCodeDestination })
        ])
      );
    }
    this.sttProductTypeForReverseJourney = form.product || "";
    this.bookingFormV4.product.productType = form.product;
    this.pupulatePartnerPOS().then(() => {
      this.bookingFormV4.commodityCipl = this.isFtz
        ? form.sttFtzCipl.length
          ? form.sttFtzCipl.map((cipl: any) => {
            return new CommodityCiplBookingV4({
              name: this.accountIsForeign
                ? cipl.item_detail_en ||
                  cipl.itemDetailEn ||
                  cipl.item_detail ||
                  cipl.itemDetail
                : cipl.item_detail || cipl.itemDetail,
              hsCode: cipl.commodity_hs_code || cipl.commodityHsCode,
              quantity: String(cipl.quantity),
              goodsPrice: formatInputByCountry(cipl.item_price || cipl.itemPrice || 0, {
                type: "money",
                country: this.accountCountryCode
              })
            });
          })
          : [
            new CommodityCiplBookingV4({
              id: 1,
              quantity: "0",
              goodsPrice: "0"
            })
          ]
        : form.sttCipl.length
          ? form.sttCipl.map((cipl: any) => {
            return new CommodityCiplBookingV4({
              name: this.accountIsForeign
                ? cipl.item_detail_en ||
                  cipl.itemDetailEn ||
                  cipl.item_detail ||
                  cipl.itemDetail
                : cipl.item_detail || cipl.itemDetail,
              hsCode: cipl.commodity_hs_code || cipl.commodityHsCode,
              quantity: String(cipl.quantity),
              goodsPrice: formatInputByCountry(cipl.item_price || cipl.itemPrice || 0, {
                type: "money",
                country: this.accountCountryCode
              })
            });
          })
          : [
            new CommodityCiplBookingV4({
              id: 1,
              quantity: "0",
              goodsPrice: "0"
            })
          ]
    });

    if (!this.bookingFormV4.commodity.name) {
      this.populateCommodity();
    }
  }

  async populateCommodity() {
    const commodityDetail = (await CommodityController.getCommodityDetail({
      id: this.bookingFormV4.commodity.id
    })) as Commodity;

    this.bookingFormV4.commodity = new CommodityBookingV4({
      id: Number(commodityDetail.commodity_id),
      name: this.isForeignAccount
        ? commodityDetail.commodity_name_en || commodityDetail.commodity_name
        : commodityDetail.commodity_name || "",
      code: commodityDetail.commodity_code || "",
      isQuarantine: commodityDetail.isQuarantine,
      isDangerousGoods: commodityDetail.isDangerousGoods
    });
  }

  errors = new ErrorBookingV4();

  countryCurrencyByCode: Record<string, string> = {};
  lastExchangeRateContextSignature = "";
  countryCurrencyRequests: Record<string, Promise<void>> = {};

  get accountIsForeign(): boolean {
    return AccountController.accountData.accountIsForeign;
  }

  get accountCountryCode(): string {
    return AccountController.accountData.countryCode;
  }

  mapSupportingDoc(res: CustomProcessEditAddressForm) {
    const additionalDocuments: AdditionalDocumentBookingV4[] = res.sttAttachFiles
      .slice(2)
      .map((image) => {
        return new AdditionalDocumentBookingV4({
          url: image
        });
      });
    const docInternational = new DocInternationalBookingV4({
      email: res.sttRecipientEmail,
      dutyTaxTerms: res.sttDutyTaxTerms as any,
      imageIdUrl: res.sttKtpImage,
      imageTaxUrl: res.sttTaxImage,
      imageAfterPackingUrl: res.sttAttachFiles.length
        ? res.sttAttachFiles[0]
        : "",
      imageBeforePackingUrl:
        res.sttAttachFiles.length >= 2 ? res.sttAttachFiles[1] : "",
      idNumber: res.sttIdentityNumber,
      taxNumber: res.sttInterTaxNumber
    });
    const ftzForm = new DocInternationalBookingV4({
      email: res.sttFtzRecipientEmail,
      idNumber: res.sttFtzIdentityNumber,
      taxNumber: res.sttTaxNumber,
      imageIdUrl: res.sttFtzKtpImage,
      imageTaxUrl: res.sttFtzTaxImage,
      imageAfterPackingUrl: res.sttFtzAttachFiles.length
        ? res.sttFtzAttachFiles[0]
        : "",
      imageBeforePackingUrl:
        res.sttFtzAttachFiles.length >= 2 ? res.sttFtzAttachFiles[1] : ""
    });

    return new SupportingDocBookingV4({
      additionalDocuments,
      docInternational,
      ftzForm
    });
  }

  resolveCountryCurrency(countryCode: string): string {
    return (
      this.countryCurrencyByCode[countryCode.toUpperCase()] ||
      getCurrencyByCountryCode(countryCode)
    );
  }

  get formLocalCurrencyCode(): string {
    return this.resolveCountryCurrency(
      this.bookingFormV4.receiver.destinationCountryCode
    );
  }

  // Country of the account/POS — static per session, not re-eval on destination change.
  get formAccountCurrencyCode(): string {
    return this.resolveCountryCurrency(this.accountCountryCode);
  }

  get formAccountCurrencyPrefix(): string {
    return configPrice[this.formAccountCurrencyCode]?.prefix ?? "Rp";
  }

  get formAccountNumericLocale(): string {
    return configPrice[this.formAccountCurrencyCode]?.locales ?? "id";
  }

  get formNumericLocale(): string {
    return configPrice[this.formAccountCurrencyCode]?.locales ?? "id";
  }

  get exchangeRatePairSignature(): string {
    return `${this.formAccountCurrencyCode}|${this.formLocalCurrencyCode}|${
      this.bookingFormV4.receiver.destinationCountryCode
    }|${this.destinationCityDetail?.id || ""}`;
  }

  get isDestinationForeign(): boolean {
    return isMalaysiaDistrict(
      this.bookingFormV4.receiver.destinationCountryCode
    );
  }

  async loadCountryCurrency(
    countryCode: string,
    countryName: string
  ): Promise<void> {
    const response = await LocationController.getCountryList({
      search: countryName,
      status: "active",
      page: 1,
      limit: 10
    });
    const country = response?.countryData.find(
      (item) => item.code.toUpperCase() === countryCode
    );
    if (!country?.currency) return;
    this.countryCurrencyByCode = {
      ...this.countryCurrencyByCode,
      [countryCode]: country.currency.toUpperCase()
    };
  }

  get isCod(): boolean {
    return this.bookingFormV4.paymentMethod.method === PaymentMethodV4.COD;
  }

  get isDfod(): boolean {
    return this.bookingFormV4.paymentMethod.method === PaymentMethodV4.DFOD;
  }

  get isRts() {
    return this.form.statusStt === "RTS";
  }

  get isCnx() {
    return this.form.statusStt === "CNX";
  }

  get isReroute() {
    return this.form.statusStt === "REROUTE";
  }

  // Metode pembayaran STT Origin (alur Reroute) — mapping sama persis dengan
  // edit-booking.vue (detail.sttIsCod/sttIsDfod/checkoutPaymentMethod/
  // sttPaymentMethod) agar DFOD & POS Limit origin tetap terpetakan benar.
  get reverseJourneyOriginPaymentMethod(): typeof PaymentMethodV4[keyof typeof PaymentMethodV4] {
    const detail = this.sttDetail;
    if (this.isRtsOrCnx || this.isReroute) return PaymentMethodV4.CASH;
    if (detail.sttIsCod && detail.sttIsDfod) return PaymentMethodV4.DFOD;
    if (detail.sttIsCod) return PaymentMethodV4.COD;
    if (
      detail.checkoutPaymentMethod === "pos_limit" ||
      detail.checkoutPaymentMethod === "invoice_limit" ||
      detail.sttPaymentMethod === "pos_limit"
    ) {
      return PaymentMethodV4.INVOICE_LIMIT;
    }
    return PaymentMethodV4.CASH;
  }

  // Opsi selector view-only: tidak menjalankan validasi fraud/blacklist/
  // kelayakan kota (pilihan hasil carry-over sistem, bukan input bebas user).
  get paymentMethodOptions(): PaymentMethodOptionV4[] {
    return [
      new PaymentMethodOptionV4({ key: PaymentMethodV4.CASH }),
      new PaymentMethodOptionV4({ key: PaymentMethodV4.COD }),
      new PaymentMethodOptionV4({ key: PaymentMethodV4.DFOD }),
      new PaymentMethodOptionV4({ key: PaymentMethodV4.INVOICE_LIMIT })
    ];
  }

  get isRtsOrCnx() {
    return this.isRts || this.isCnx || this.rtsOrCnx;
  }

  get isRtshq() {
    return this.form.statusStt === "RTSHQ";
  }

  get sttCodRetail() {
    const sttNumber = this.form.rootReverseJourneySttNo || this.form.sttNumber;
    return !!(
      sttNumber.toLocaleLowerCase().match(/^11lp/gi) ||
      sttNumber.toLowerCase().match(/^10lp/gi)
    );
  }

  get sttCodCaRetail() {
    return !!this.form.shipmentId.match(/^ARA|^ARB/gi);
  }

  get isCodRetail(): boolean {
    return (
      this.isCod && this.isRts && (this.sttCodRetail || this.sttCodCaRetail)
    );
  }

  get isShipment88Lp(): boolean {
    const sttNumber = this.form.rootReverseJourneySttNo || this.form.sttNumber;
    return !!sttNumber.toLowerCase().match(/^88lp/gi);
  }

  // --- Proteksi, Harga Barang & Packing Kayu (GENESIS-20217) ---
  get originGoodsPrice(): number | null {
    const detail = this.sttDetail;
    return (
      detail.sttGoodsEstimatePriceForDetail ||
      Number(
        String(detail.sttGoodsEstimatePrice || 0).replace(/[^\d.]/g, "")
      ) ||
      null
    );
  }

  get originCodAmount(): number | null {
    const detail = this.sttDetail;
    return (
      detail.sttCodAmountForDetail ||
      Number(String(detail.sttCodAmount || 0).replace(/[^\d]/g, "")) ||
      null
    );
  }

  // Packing kayu added-weight preview (formula mirrors edit-booking.vue) using
  // the origin STT chargeable weight as the base CW, since the reverse-journey
  // modal carries no editable package weights.
  // get woodpackingAdditionalWeightKgPreview(): number {
  //   const { protectionPacking } = this.bookingFormV4;
  //   if (!protectionPacking.woodpackingEligible) return 0;
  //   const base = Number(
  //     this.sttDetail.sttTotalChargeAbleWeight ||
  //       this.sttDetail.sttChargeableWeight ||
  //       0
  //   );
  //   if (!base) return 0;
  //   const { cwPercent, minCwKg } =
  //     BookingController.woodpackingEligibility || {};
  //   return Math.max(base * ((cwPercent || 0) / 100), minCwKg || 0);
  // }

  // async fetchWoodpackingEligibility(): Promise<void> {
  //   const originDistrict = this.bookingFormV4.sender.originCity;
  //   const origin3lc =
  //     typeof originDistrict === "string" ? "" : originDistrict?.cityCode;
  //   const productType = this.form.product;
  //   const accountRef = String(this.sttDetail.sttBookedForId || "");

  //   if (!origin3lc || !productType) {
  //     this.bookingFormV4.protectionPacking.woodpackingEligible = false;
  //     return;
  //   }

  //   let eligible = false;
  //   try {
  //     const response = await BookingController.getWoodpackingEligibility(
  //       new WoodpackingEligibilityQuery({
  //         origin3lc,
  //         productType: productType.toUpperCase().trim(),
  //         accountRef
  //       })
  //     );
  //     eligible = Boolean(response && response.eligible);
  //   } catch (error) {
  //     eligible = false;
  //   }

  //   const { protectionPacking } = this.bookingFormV4;
  //   protectionPacking.woodpackingEligible = eligible;
  //   if (!eligible && protectionPacking.woodpacking) {
  //     protectionPacking.woodpacking = false;
  //   }
  //   protectionPacking.woodpackingAdditionalWeightKg = this.woodpackingAdditionalWeightKgPreview;
  // }

  get shipmentC1SpecialCod() {
    return (
      this.form.shipmentId.startsWith(
        FlagsFeatureShipmentC1.feature_shipment_c1.getValue()
      ) &&
      this.form.sttIsCod.toLowerCase() === "yes" &&
      this.form.codHandling.toLowerCase() === "specialcod"
    );
  }

  get shipmentFavoritCodDfod() {
    return !!(
      this.form.shipmentId.match(/^AP|^AS/gi) &&
      (this.form.sttIsCod.toLowerCase() === "yes" || this.form.sttIsDfod) &&
      this.form.statusStt !== "REROUTE"
    );
  }

  // CONFIG RULE DISABLED
  configRuleAdjustStt = new SttReferenceAdjustment();

  get configRule(): SttAdjustment {
    const disabledRule = new SttAdjustmentRule({
      isRequired: true,
      rule: ConfigValidationForm.NON_EDITABLE
    });

    this.configRuleAdjustStt.config = new SttAdjustment({
      senderName: disabledRule,
      senderPhone: disabledRule,
      senderAddress: disabledRule,
      senderDistrict: disabledRule,
      recipientName: this.disabledDetailReceiver,
      recipientPhone: this.disabledDetailReceiver,
      recipientAddress: this.disabledDetailReceiver,
      recipientDistrict: this.disabledDetailReceiver
    });

    return this.configRuleAdjustStt.config;
  }

  get disabledDetailReceiver() {
    if (this.isReroute) return new SttAdjustmentRule({
        isRequired: true,
        rule: ConfigValidationForm.EDITABLE
      });
    if (
      [
        this.disableCodRetail,
        this.shipmentC1SpecialCod,
        this.shipmentFavoritCodDfod
      ].includes(true)
    ) {
      return new SttAdjustmentRule({
        isRequired: true,
        rule: ConfigValidationForm.NON_EDITABLE
      });
    } else {
      return new SttAdjustmentRule({
        isRequired: true,
        rule: ConfigValidationForm.EDITABLE
      });
    }
  }

  get disableCodRetail() {
    return (
      this.form.sttIsCod.toLowerCase() === "yes" &&
      this.isRts &&
      (this.sttCodRetail || this.sttCodCaRetail)
    );
  }

  // --- Pengirim/Penerima wiring (GENESIS-20155) — sequential tab-order
  // constants mirroring create-booking-v3/create-booking-manual.vue's inline
  // indices (sender 1-4, receiver 5-10); shared currentIndex drives focus
  // ordering across both sections (mirror v3's single `currentIndex`).

  partnerIndex = 0;
  senderCountryIndex = 1;
  senderNameIndex = 2;
  senderPhoneIndex = 3;
  senderAddressIndex = 4;
  receiverCountryIndex = 5;
  receiverNameIndex = 6;
  receiverPhoneIndex = 7;
  receiverAddressIndex = 8;
  receiverDistrictIndex = 9;
  receiverPostalCodeIndex = 10;

  currentIndex = -1;

  onSectionCurrentIndexUpdate(index: number): void {
    this.currentIndex = index;
  }

  // --- Form completion state engine (GENESIS-19583) ---
  // Section validity placeholders: GENESIS-20156 (reference), 20157
  // (barang/koli) and 20158 (dokpend) mount their section components and
  // assign real values here from `@validationChange` — until then these
  // default incomplete so formCompletionItems / the panel reflect a
  // not-yet-filled form rather than a fabricated "complete" state.
  // Sender/Receiver wired by GENESIS-20155 — driven by
  // onSenderValidationChange / onReceiverValidationChange above.
  isSenderSectionValid = false;
  isReceiverSectionValid = false;
  isReferenceSectionValid = false;
  isCommoditySectionValid = false;
  isPackageWeightSectionValid = false;
  isCiplSectionValid = false;
  isSupportingDocSectionValid = false;
  supportingDocFieldRulesReady = false;
  supportingDocRulesRequestId = 0;
  configurationRules: ProductConfigurationRule[] = [];

  // SenderSectionV4/ReceiverSectionV4 mutate `modelValue` (the full
  // CreateBookingV4 object, passed by reference) in place then re-emit it —
  // reassigning here keeps bookingFormV4 the single source of truth per the
  // detailing's parent-wiring contract (child never calls BookingV4Controller).
  errorRouteNotFound = false;

  sttBookedBy = 0;

  onSenderUpdate(value: CreateBookingV4): void {
    this.bookingFormV4 = value;
  }

  onSenderValidationChange(valid: boolean): void {
    this.isSenderSectionValid = valid;
  }

  onReceiverValidationChange(valid: boolean): void {
    this.isReceiverSectionValid = valid;
  }

  onReceiverUpdate(value: CreateBookingV4): void {
    this.bookingFormV4 = value;
    void this.syncActiveExchangeRate();
  }

  get destinationCityDetail(): DistrictData | null {
    const destination = this.bookingFormV4.receiver.destinationCity;
    const result = typeof destination === "string" ? null : destination;
    const origin = this.bookingFormV4.sender.originCity;
    if (
      result instanceof DistrictData &&
      result.countryCode &&
      origin instanceof DistrictData &&
      origin.countryCode &&
      result.countryCode != origin.countryCode
    ) {
      this.isCipl = true;
    } else {
      this.isCipl = false;
    }
    return result;
  }

  async syncActiveExchangeRate(): Promise<void> {
    const destinationCountryCode = this.bookingFormV4.receiver
      .destinationCountryCode;
    const destinationCityId = this.destinationCityDetail?.id || 0;
    if (!destinationCountryCode || !destinationCityId) {
      await BookingV4Controller.clearActiveExchangeRate();
      return;
    }
    await Promise.all([
      this.fetchCountryCurrency(this.accountCountryCode),
      this.fetchCountryCurrency(destinationCountryCode)
    ]);
    const from = this.formAccountCurrencyCode;
    const to = this.formLocalCurrencyCode;
    const contextSignature = this.exchangeRatePairSignature;
    if (contextSignature === this.lastExchangeRateContextSignature) return;
    this.lastExchangeRateContextSignature = contextSignature;
    if (from === to) {
      await BookingV4Controller.clearActiveExchangeRate();
      return;
    }
    const result = await BookingV4Controller.fetchActiveExchangeRate({
      from,
      to
    });
    if (!result) this.lastExchangeRateContextSignature = "";
  }

  get isErrorEmbargoDestination() {
    return (
      this.productUpsellingData.length > 0 &&
      this.productUpsellingData.every((item) => item.isEmbargo) &&
      !!this.bookingFormV4.receiver.destinationCity
    );
  }

  get disableReceiver() {
    if (this.isRts) {
      return this.isCod || this.isDfod;
    }

    return false;
  }

  get allowPasteReceiverDetail() {
    return !this.isRts && !this.isCnx && !this.isReroute;
  }

  // Sender emits @autofillCommodity (GENESIS-19570 -> GENESIS-20155 forward)
  // when the user picks a saved customer whose commodity autofills. Distinct
  // from applyCommodityPrefillRules(): the source is customer data, not CIPL,
  // so §3.7's prefill rules never run here (per detailing's explicit note).
  commodityUserOverride = false;

  async onAutofillCommodity(): Promise<void> {
    await this.refreshCommodityRecommendations();
    this.validateCommodityActive(this.bookingFormV4.commodity);
    this.commodityUserOverride = false;
  }

  mapCommodityListToBookingV4(list: Commodity[]): CommodityBookingV4[] {
    return list.map(
      (item) =>
        new CommodityBookingV4({
          id: item.commodity_id,
          name: item.commodity_name,
          nameEn: item.commodity_name_en,
          code: item.commodity_code,
          isDangerousGoods: item.isDangerousGoods,
          isQuarantine: item.isQuarantine,
          status: item.commodity_status
        })
    );
  }

  validateCommodityActive(commodity: CommodityBookingV4): void {
    const isInactive =
      !!commodity.id && !!commodity.status && commodity.status !== "active";
    this.errors.commodity = isInactive
      ? (this.$t("bookingV4.goods.commodity.errorInactive") as string)
      : "";
  }

  async fetchCountryCurrency(countryCode: string): Promise<void> {
    const normalizedCode = countryCode.toUpperCase();
    if (this.countryCurrencyByCode[normalizedCode]) return;
    if (this.countryCurrencyRequests[normalizedCode]) {
      return this.countryCurrencyRequests[normalizedCode];
    }

    const countryName = COUNTRY_OPTIONS.find(
      (country) => country.countryCode === normalizedCode
    )?.name;
    if (!countryName) return;

    const request = this.loadCountryCurrency(normalizedCode, countryName);
    this.countryCurrencyRequests[normalizedCode] = request;
    try {
      await request;
    } finally {
      delete this.countryCurrencyRequests[normalizedCode];
    }
  }

  // COMMODITY
  readonly ENABLE_DUTY_TAX_INFOBAR = false;
  ciplCommodityOptions: CommodityBookingV4[] = [];

  commoditySearchResults: CommodityBookingV4[] = [];
  commodityRecommendations: CommodityBookingV4[] = [];
  commodityListLoading = false;
  commodityListPage = 1;
  commodityListTotalData = 0;

  modalSearchResults: CommodityBookingV4[] = [];
  commodityModalLoading = false;
  commodityModalPage = 1;
  commodityModalTotalData = 0;

  commodityCategories: CommodityCategoryV4[] = [];
  commodityCategoryItems: CommodityBookingV4[] = [];
  selectedCategoryCode: string | null = null;
  commodityModalLoadingCategory = false;

  async fetchCiplCommodityOptions(): Promise<void> {
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          limit: 100,
          countryCode: this.accountCountryCode.toUpperCase(),
          commodityStatus: "active"
        })
      );
      this.ciplCommodityOptions = this.mapCommodityListToBookingV4(resp.data);
    } catch (error) {
      this.ciplCommodityOptions = [];
    }
  }

  get ciplLocalTotal(): number {
    return this.bookingFormV4.commodityCipl.reduce((total, item) => {
      return (
        total +
        parseCiplNumber(item.quantity) * parseCiplNumber(item.goodsPrice)
      );
    }, 0);
  }

  get activeCiplExchangeRate(): number | null {
    const rate = BookingV4Controller.activeExchangeRate;
    const from = this.formAccountCurrencyCode;
    const to = this.formLocalCurrencyCode;
    const isValid = Boolean(
      rate &&
        rate.exchangeRateFrom === from &&
        rate.exchangeRateTo === to &&
        rate.exchangeRateStatus === "active" &&
        Number.isFinite(rate.exchangeRate) &&
        rate.exchangeRate > 0
    );
    return isValid && rate ? rate.exchangeRate : null;
  }

  get ciplEstimatedConversion(): { currency: string; amount: number } | null {
    const rate = this.activeCiplExchangeRate;
    const isIdrToMyr =
      this.formAccountCurrencyCode === "IDR" &&
      this.formLocalCurrencyCode === "MYR";
    const estimatedConversion =
      rate === null
        ? null
        : {
            currency: this.formLocalCurrencyCode,
            amount: isIdrToMyr
              ? this.ciplLocalTotal / rate
              : this.ciplLocalTotal * rate
          };
    return estimatedConversion;
  }

  get ciplDeminimisThreshold(): number | null {
    if (!this.ENABLE_DUTY_TAX_INFOBAR) return null;
    return null; // TODO(GENESIS-20095): resolve from duty/tax config
  }

  get recipientImportDocRequired(): boolean {
    if (!this.ENABLE_DUTY_TAX_INFOBAR) return false;
    return false; // TODO(GENESIS-20095): resolve from duty/tax config
  }

  async searchCommodity(keyword: string): Promise<void> {
    this.commodityListLoading = true;
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: 1,
          limit: 20,
          commodityStatus: "active",
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.commoditySearchResults = this.mapCommodityListToBookingV4(resp.data);
      this.commodityListPage = 1;
      this.commodityListTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      this.commoditySearchResults = [];
      this.commodityListTotalData = 0;
    } finally {
      this.commodityListLoading = false;
    }
  }

  async refreshCommodityRecommendations(): Promise<void> {
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          isRecommendedOnly: true,
          limit: 6,
          countryCode: this.accountCountryCode.toUpperCase(),
          commodityStatus: "active"
        })
      );
      this.commodityRecommendations = this.mapCommodityListToBookingV4(
        resp.data
      ).slice(0, 6);
    } catch (error) {
      this.commodityRecommendations = [];
    }
  }

  async fetchCommodityCategories(): Promise<void> {
    try {
      const categories = await CommodityController.getCommodityCategory(
        this.accountCountryCode.toUpperCase()
      );
      this.commodityCategories = categories.map(
        (item) =>
          ({
            code: item.commodityCategoryCode,
            name: item.commodityCategoryNameId,
            nameEn: item.commodityCategoryNameEn
          } as CommodityCategoryV4)
      );
    } catch (error) {
      this.commodityCategories = [];
    }
  }

  async loadMoreCommodities(keyword: string): Promise<void> {
    if (!this.commodityListHasMore || this.commodityListLoading) return;
    this.commodityListLoading = true;
    try {
      const nextPage = this.commodityListPage + 1;
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: nextPage,
          limit: 20,
          countryCode: this.accountCountryCode.toUpperCase(),
          commodityStatus: "active"
        })
      );
      this.commoditySearchResults = [
        ...this.commoditySearchResults,
        ...this.mapCommodityListToBookingV4(resp.data)
      ];
      this.commodityListPage = nextPage;
      this.commodityListTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      // Keep the already-loaded page(s); just stop paginating on failure.
    } finally {
      this.commodityListLoading = false;
    }
  }

  async searchCommodityModal(keyword: string): Promise<void> {
    this.commodityModalLoading = true;
    this.selectedCategoryCode = null;
    this.commodityCategoryItems = [];
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: 1,
          limit: 20,
          commodityStatus: "active",
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.modalSearchResults = this.mapCommodityListToBookingV4(resp.data);
      this.commodityModalPage = 1;
      this.commodityModalTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      this.modalSearchResults = [];
      this.commodityModalTotalData = 0;
    } finally {
      this.commodityModalLoading = false;
    }
  }

  async loadMoreCommodityModal(keyword: string): Promise<void> {
    if (!this.commodityModalHasMore || this.commodityModalLoading) return;
    this.commodityModalLoading = true;
    try {
      const nextPage = this.commodityModalPage + 1;
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: nextPage,
          limit: 20,
          countryCode: this.accountCountryCode.toUpperCase(),
          commodityStatus: "active"
        })
      );
      this.modalSearchResults = [
        ...this.modalSearchResults,
        ...this.mapCommodityListToBookingV4(resp.data)
      ];
      this.commodityModalPage = nextPage;
      this.commodityModalTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      // Keep the already-loaded page(s); just stop paginating on failure.
    } finally {
      this.commodityModalLoading = false;
    }
  }

  async selectCommodityCategory(code: string): Promise<void> {
    this.selectedCategoryCode = code;
    this.commodityModalLoadingCategory = true;
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          commodityCategoryCode: code,
          page: 1,
          limit: 100,
          countryCode: this.accountCountryCode.toUpperCase(),
          commodityStatus: "active"
        })
      );
      this.commodityCategoryItems = this.mapCommodityListToBookingV4(resp.data);
    } catch (error) {
      this.commodityCategoryItems = [];
    } finally {
      this.commodityModalLoadingCategory = false;
    }
  }

  onCommodityCiplUpdate(value: CommodityCiplBookingV4[]): void {
    this.bookingFormV4.commodityCipl = value;
    this.onSectionCurrentIndexUpdate(11);
  }

  onCommodityError(errorMessage: string) {
    this.errors.commodity = errorMessage;
  }

  get commodityListHasMore(): boolean {
    return this.commodityListPage * 20 < this.commodityListTotalData;
  }

  get commodityModalHasMore(): boolean {
    return this.commodityModalPage * 20 < this.commodityModalTotalData;
  }

  isShowSectionUpload = false;

  onCommodityUpdate(value: CommodityBookingV4 | null): void {
    this.bookingFormV4.commodity = value || new CommodityBookingV4();
    this.commodityUserOverride = true;
    this.onSectionCurrentIndexUpdate(11);
  }

  onGoodsPackageReset(): void {
    this.bookingFormV4.commodityCipl = [];
    this.bookingFormV4.commodity = new CommodityBookingV4();
    this.bookingFormV4.commodityCipl = this.showItemDetails
      ? [new CommodityCiplBookingV4({
          id: 1,
          quantity: "0",
          goodsPrice: "0"
        })]
      : [];
    this.commodityUserOverride = false;
    this.errors.commodity = "";
    this.isCiplSectionValid = !this.showItemDetails;
    this.isCommoditySectionValid = false;
  }

  onCommodityValidationChange(isValid: boolean): void {
    this.isCommoditySectionValid = isValid;
  }

  filterPartners = debounce(async (search: string, isSearchBy3LC = false) => {
    this.isLoadingPartners = true;
    const resp = await PartnerController.fetchPartnerApi({
      search: search,
      page: 1,
      limit: 10,
      type: "pos",
      parentId: this.parentId,
      status: "not-banned"
    });
    this.partnersData = resp.filter((key: ClientPartnerOptions) =>
      key.contractStatus.match(/^active|^nearly-expired/gi)
    );
    this.isLoadingPartners = false;
    this.onSelectCurrentIndex(this.partnerIndex);
  }, 250);

  onChangePartner = debounce((value: ClientPartnerOptions) => {
    this.selectedPartnerPos = value;
    this.sttBookedBy = value.id;
  });

  async pupulatePartnerPOS() {
    try {
      this.isLoadingPartnerField = true;
      const partnerDetail = await PartnerController.fetchPartnerDetail({
        id: this.parentId,
        tieringLevel: false
      });
      if (partnerDetail.partnerIdSttReturn) {
        this.disableSelectPartnerField = !!partnerDetail.partnerIdSttReturn;
        this.selectedPartnerPos = {
          id: partnerDetail.partnerIdSttReturn,
          name: partnerDetail.partnerNameSttReturn
        };
      } else {
        this.disableSelectPartnerField = false;
        this.selectedPartnerPos = {
          id: this.posIDForInternal,
          name: this.posNameForInternal
        };
      }
      if (partnerDetail.partnerIdSttReturn) {
        this.sttBookedBy = partnerDetail.partnerIdSttReturn;
      }
      this.populateOriginData(partnerDetail);
    } finally {
      this.isLoadingPartnerField = false;
    }
  }

  async populateOriginData(partnerDetail: PartnerDetail) {
    // RTS or CNX
    let originDistrict = this.form.originCity.code;
    if (this.isRtsOrCnx) {
      const partnerPhoneNumber = partnerDetail.partnerPhoneNumber.startsWith(
        "62"
      )
        ? partnerDetail.partnerPhoneNumber.slice(2)
        : partnerDetail.partnerPhoneNumber;
      const phoneCode = codePhoneCountry(partnerDetail.partnerPhoneNumber).code;
      this.bookingFormV4.sender.name = partnerDetail.partnerName;
      this.bookingFormV4.sender.phone = partnerPhoneNumber;
      this.bookingFormV4.sender.phoneCode = !phoneCode.includes("62")
        ? "+62"
        : phoneCode;
      this.bookingFormV4.sender.address = partnerDetail.partnerAddress;
    }
    originDistrict = partnerDetail.partnerDistrictCode;

    const origin = (await LocationController.getDetailDistrictByDistrictCode({
      districtCode: originDistrict
    })) as DistrictData;
    const hasCountryCodes =
      Boolean(this.form.sttOriginCountryCode) &&
      Boolean(this.form.sttDestinationCountryCode);
    const internationalSTT =
      hasCountryCodes &&
      this.form.sttOriginCountryCode !== this.form.sttDestinationCountryCode;
    this.isFtz =
      AccountController.accountData.account_location?.city_free_trade_zone ===
        "yes" && !internationalSTT;
    this.isCipl = internationalSTT;
    this.bookingFormV4.sender.originCity = origin;

    // fetch destination detail
    if (this.bookingFormV4.receiver.destinationCity instanceof DistrictData && !this.isReroute) {
      const destination = (await LocationController.getDetailDistrictByDistrictCode(
        {
          districtCode: this.bookingFormV4.receiver.destinationCity.code
        }
      )) as DistrictData;
      this.bookingFormV4.receiver.destinationCity = destination;
      await this.$nextTick();
      this.$refs.receiverDetail.onSelectDestinationCity()
      await this.$nextTick();
      this.bookingFormV4.receiver.postalCode = new DistrictData({
        name: destination.destinationZipCode.split(",")[0]
      });
    }

    // this.fetchWoodpackingEligibility();
    this.sdnReady = true;
  }

  onCiplValidationChange(isValid: boolean): void {
    this.isCiplSectionValid = isValid;
  }

  onSupportingDocUpdate(value: SupportingDocBookingV4): void {
    this.bookingFormV4.supportingDoc = value;
  }

  onSupportingDocValidationChange(valid: boolean): void {
    this.isSupportingDocSectionValid =
      this.supportingDocFieldRulesReady && valid;
  }

  getEstimationPriceDebounced = debounce(() => {
    this.getEstimationPrice();
  }, 500);

  resetEstimationPrice(): void {
    this.estimationRequestId += 1;
    this.bookingFormV4.estimationPrice = new EstimationPrice();
    this.bookingFormV4.protectionPacking.woodpackingAdditionalWeightKg = 0;
    this.estimationLoading = false;
    this.estimationIsError = false;
  }

  async getEstimationPrice(): Promise<void> {
    if (!this.isTariffContextReady) {
      this.resetEstimationPrice();
      return;
    }

    const requestId = ++this.estimationRequestId;

    try {
      this.estimationLoading = true;
      this.estimationIsError = false;
      if (this.productUpsellingLoading) {
        this.fetchPromisesAfterGetUpselling.push("getEstimationPriceDebounced");
        return;
      }
      const estimationPrice = await BookingV4Controller.getEstimationPrice(
        this.estimationPricePayload
      );
      if (requestId !== this.estimationRequestId) return;
      this.bookingFormV4.estimationPrice = estimationPrice;
      // this.bookingFormV4.protectionPacking.woodpackingAdditionalWeightKg =
      //   estimationPrice.woodpackingAdditionalWeight;
    } catch (error) {
      if (requestId !== this.estimationRequestId) return;
      this.estimationIsError = true;
    } finally {
      if (requestId === this.estimationRequestId) {
        this.estimationLoading = false;
      }
    }
  }

  onTariffRefresh(): void {
    this.getEstimationPrice();
  }

  // Submit adapter (GENESIS-21529): the Booking CTA validates the required
  // fields, then maps the working CreateBookingV4 back into the
  // CustomProcessEditAddressForm the parent's Proses payload reads from —
  // no HTTP request happens here, the modal only emits.
  get isAllSectionValid(): boolean {
    const isPosValid = this.isRtsOrCnx ? !!this.selectedPartnerPos?.id : true;
    const isCommodityValid =
      this.isCommoditySectionValid && !this.errors.commodity;
    const isSupportingDocValid =
      !this.showItemDetails || this.isSupportingDocSectionValid;

    return (
      this.isSenderSectionValid &&
      this.isReceiverSectionValid &&
      isCommodityValid &&
      (!this.showItemDetails || this.isCiplSectionValid) &&
      isSupportingDocValid &&
      !!this.bookingFormV4.product.productType &&
      isPosValid
    );
  }

  buildEditAddressForm(): CustomProcessEditAddressForm {
    const form = this.bookingFormV4;
    const receiver = form.receiver;
    const sender = form.sender;
    const postalCode =
      typeof receiver.postalCode === "string"
        ? receiver.postalCode
        : receiver.postalCode?.name || this.form.postalCode;
    const supportingDoc = form.supportingDoc;
    const activeDoc = this.isFtz
      ? supportingDoc.ftzForm
      : supportingDoc.docInternational;

    const mapCipl = (items: any[]): CIPLTypeV4[] =>
      items
        .filter((item) => item.name)
        .map((item) => ({
          quantity: parseCiplNumber(item.quantity),
          itemPrice: parseByCountry(item.goodsPrice, {
            country: this.accountCountryCode
          }),
          itemDetail: item.commodity?.nameId || item.name || "",
          itemDetailEn: item.commodity?.nameEn || item.name || "",
          commodityHsCode: item.commodity?.hsCode || item.hsCode || ""
        }));

    const sttCipl = this.isFtz ? [] : mapCipl(form.commodityCipl);
    const ftzCipl = this.isFtz ? mapCipl(form.commodityCipl) : [];
    const internationalDoc = supportingDoc.docInternational;
    const ftzDoc = supportingDoc.ftzForm;

    return new CustomProcessEditAddressForm({
      ...this.form,
      senderName: sender.name,
      sttSenderPhoneCode: sender.phoneCode,
      senderPhone: sender.phone,
      senderAddress: sender.address,
      originCity: sender.originCity,
      consigneeName: receiver.name,
      sttRecipientPhoneCode: receiver.phoneCode,
      consigneePhone: receiver.phone,
      consigneeAddress: receiver.address,
      addressType: receiver.addressType.value,
      destinationCity: receiver.destinationCity,
      postalCode,
      product: form.product.productType,
      commodity: new Commodity({
        commodity_id: form.commodity.id,
        commodity_name: form.commodity.name,
        commodity_code: form.commodity.code,
        isQuarantine: form.commodity.isQuarantine,
        isDangerousGoods: form.commodity.isDangerousGoods
      }),
      sttCommodityCode: form.commodity.code,
      sttRecipientEmail: internationalDoc.email,
      sttKtpImage: this.getSupportingDocUrl(internationalDoc.imageIdUrl),
      sttTaxImage: this.getSupportingDocUrl(internationalDoc.imageTaxUrl),
      sttIdentityNumber: internationalDoc.idNumber,
      sttInterTaxNumber: internationalDoc.taxNumber,
      sttAttachFiles: this.getSupportingDocUrls(internationalDoc, !this.isFtz),
      sttCipl,
      sttFtzRecipientEmail: ftzDoc.email,
      sttFtzIdentityNumber: ftzDoc.idNumber,
      sttFtzKtpImage: this.getSupportingDocUrl(ftzDoc.imageIdUrl),
      sttFtzTaxImage: this.getSupportingDocUrl(ftzDoc.imageTaxUrl),
      sttFtzAttachFiles: this.getSupportingDocUrls(ftzDoc, this.isFtz),
      sttFtzCipl: ftzCipl,
      sttTaxNumber: ftzDoc.taxNumber,
      taxNumber: ftzDoc.taxNumber,
      sttDutyTaxTerms: activeDoc.dutyTaxTerms || "",
      sttBookedBy: this.sttBookedBy,
      insurance: this.bookingFormV4.protectionPacking.insuranceType,
      isNewForm: true
    });
  }

  getSupportingDocUrls(
    document: DocInternationalBookingV4,
    includeAdditionalDocuments: boolean
  ): string[] {
    const additionalUrls = includeAdditionalDocuments
      ? this.bookingFormV4.supportingDoc.additionalDocuments.map(
          (item) => item.url
        )
      : [];
    const urls: unknown[] = [
      document.imageBeforePackingUrl,
      document.imageAfterPackingUrl,
      ...additionalUrls
    ];
    return urls.map((url) => this.getSupportingDocUrl(url)).filter(Boolean);
  }

  getSupportingDocUrl(value: unknown): string {
    if (typeof value !== "string" || value.startsWith("blob:")) return "";
    return value;
  }

  async onSave() {
    this.currentIndex = 99;
    this.hasAttemptedSubmit = true;
    this.validateCommodityActive(this.bookingFormV4.commodity);

    if (!this.isAllSectionValid) {
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          title: this.$t("bookingV4.validationError") as string,
          message: this.$t("bookingV4.completeForm") as string,
          type: CLIENT_ERROR,
          onTryAgain: () => {
            MainAppController.closeErrorMessage();
          }
        })
      );
      return;
    }

    this.$emit("onSubmit", this.buildEditAddressForm());

    if (!this.disableSelectPartnerField)
      this.$emit("onChangePartner", this.selectedPartnerPos);
    this.closeModal();
  }

  // --- Jenis Pengiriman / ProductCardListV4 wiring (GENESIS-20216) ---
  productUpsellingData: PosterProduct[] = [];
  // Product type dari STT asal (dibalikan) — dipakai sebagai acuan mapping
  // autopopulate PRD §3.8 sebelum user memilih kartu lain.
  sttProductTypeForReverseJourney = "";

  get listProducts(): string[] {
    const detail = this.sttDetail;
    if (detail?.sttBookedForType === "client") {
      let clientProduct: string[] =
        this.accountCountryCode.toUpperCase() === "MY"
          ? FlagsPermissionBookingV3.config_upselling_product_list.getValue()
              .countries.MY.edit_client
          : FlagsPermissionBookingV3.config_upselling_product_list.getValue()
              .edit_client;
      if (!FlagsPermissionBooking.config_show_bigpack_fast.isEnabled()) {
        clientProduct = clientProduct.filter(
          (product) => product !== "BIGPACKFAST"
        );
      }
      return clientProduct;
    }
    let retailProducts: string[] =
      this.accountCountryCode.toUpperCase() === "MY"
        ? FlagsPermissionBookingV3.config_upselling_product_list.getValue()
            .countries.MY.edit_retail
        : FlagsPermissionBookingV3.config_upselling_product_list.getValue()
            .edit_retail;
    if (
      !AccountController.accountData.account_type_detail
        ?.partnerIsAllowBookVippack &&
      AccountController.accountData.account_type === "partner"
    ) {
      retailProducts = retailProducts.filter((p) => p !== "VIPPACK");
    }
    if (!FlagsPermissionBooking.config_show_bigpack_fast.isEnabled()) {
      retailProducts = retailProducts.filter(
        (product) => product !== "BIGPACKFAST"
      );
    }
    return retailProducts;
  }

  get reorderProductTypes() {
    const listProduct = this.listProducts;
    let upselling = this.productUpsellingData;

    // Reverse journey tanpa tab Domestik/Internasional — seluruh produk yang
    // dikembalikan API (domestik & internasional) ditampilkan dalam satu grid.
    // Daftar produk dikonfigurasi via Unleash BCS config_product_by_type_rj
    // (flat array per negara ID/MY).
    const productByType = FlagsPermissionBookingV4.config_product_by_type_rj.getValue();
    const availableTypes = new Set<string>(
      productByType[this.formAccountNumericLocale.toUpperCase()] ??
        productByType.ID ??
        []
    );
    upselling = upselling.filter((item) =>
      availableTypes.has(item.productType.toUpperCase())
    );

    const filteredListProduct = listProduct.filter((p) =>
      availableTypes.has(p)
    );

    const orderedTypes = applyRecommendationOrder(
      filteredListProduct,
      upselling
    );

    return {
      list: orderedTypes,
      upselling
    };
  }

  get productCardItems(): ProductCardItem[] {
    const { list, upselling } = this.reorderProductTypes;
    const hasRecommended = upselling.some((p) => p.isRecommended);

    return list
      .map((type) => {
        const poster = upselling.find(
          (item) => item.productType.toUpperCase() === type.toUpperCase()
        );
        if (!poster)
          return new PosterProduct({
            productType: type,
            isRecommended: !hasRecommended && type.toUpperCase() === "BOSSPACK"
          });

        const isRecommended = !hasRecommended
          ? poster.productType.toUpperCase() === "BOSSPACK"
          : Boolean(poster.isRecommended);

        const slaParts = (poster.estimateSla || "")
          .replace(/\s*Hari\s*/i, "")
          .split("-")
          .map((s) => parseInt(s.trim(), 10));
        const slaMinDays = Number.isFinite(slaParts[0]) ? slaParts[0] : 0;
        const slaMaxDays = Number.isFinite(slaParts[1])
          ? slaParts[1]
          : slaMinDays;

        return {
          productType: poster.productType.toUpperCase(),
          isAvailable: poster.totalTariff != null && poster.totalTariff > 0,
          isEmbargo: poster.isEmbargo ?? false,
          isRecommended,
          shippingCost: poster.totalTariff ?? null,
          slaMinDays,
          slaMaxDays,
          commissionRate: poster.commissionRate ?? 0,
          commissionAmount: poster.bookingCommission ?? 0,
          showMinWeightNote:
            poster.productType === "BIGPACK" ||
            poster.productType === "BIGPACKFAST"
        } as ProductCardItem;
      })
      .filter((item): item is ProductCardItem => item !== null);
  }

  // Mapping autopopulate PRD §3.8 — kode produk STT asal -> produk awal yang
  // dipilih pada rute tujuan balikan. Semua kelompok di bawah REGPACK yang
  // mereduksi ke REGPACK, sementara INTERMAXI/INTERPACKMAXI -> INTERPACK.
  resolveDefaultProductForReverseJourney(
    sttProductType: string,
    products: ProductCardItem[]
  ): string {
    const mapping: Record<string, string> = {
      ONEPACK: "REGPACK",
      BOSSPACK: "BOSSPACK",
      REGPACK: "REGPACK",
      JAGOPACK: "JAGOPACK",
      BIGPACK: "BIGPACK",
      BIGPACKFAST: "BIGPACKFAST",
      MINIPACK: "MINIPACK",
      VIPPACK: "VIPPACK",
      INTERPACK: "INTERPACK",
      INTERPACKMAXI: "INTERPACKMAXI"
    };
    const normalized = (sttProductType || "").toUpperCase();
    const mapped = mapping[normalized];

    const isAvailable = (type: string): boolean =>
      products.some(
        (p) =>
          p.productType.toUpperCase() === type.toUpperCase() && p.isAvailable
      );

    // Jika hasil mapping tersedia pada rute tujuan, pilih produk tsb.
    if (mapped && isAvailable(mapped)) return mapped;

    // Fallback opsi teratas yang tersedia bila produk hasil mapping tidak
    // tercover pada rute tujuan (DoD: "opsi paling atas yang tersedia").
    const topAvailable = products.find((p) => p.isAvailable);
    if (topAvailable) return topAvailable.productType.toUpperCase();
    return products[0]?.productType.toUpperCase() || "";
  }

  onProductTypeSelect(productType: string): void {
    this.bookingFormV4.product.productType = productType;
  }

  async onProductUpsellingRetry(): Promise<void> {
    await this.fetchProductUpselling();
  }

  get productUpsellingContextSignature(): string {
    return JSON.stringify(this.upsellingPayload);
  }

  get upsellingPayload() {
    const destination = this.destinationCityDetail;
    const paymentMethod = this.bookingFormV4.paymentMethod;

    return new PayloadPosterProduct({
      originId: this.districtDetail.code || "",
      destinationId: destination?.code || "",
      accountType: "pos",
      accountRefId: 0,
      commodityId: this.bookingFormV4.commodity.id || 499,
      commodity: {
        data: new ComodityProductPoster({
          commodityId: this.bookingFormV4.commodity.id,
          commodityCode: this.bookingFormV4.commodity.code,
          commodityName: this.bookingFormV4.commodity.name
        })
      },
      productType: "",
      goodsPrice: this.bookingFormV4.protectionPacking.itemPrice || 0,
      codAmount: paymentMethod?.codAmount ?? 0,
      isCod: false,
      isDfod: false,
      isPad: false,
      insuranceType: this.bookingFormV4.protectionPacking.insuranceType,
      isWoodpacking: this.bookingFormV4.protectionPacking.woodpacking,
      isHaveTaxId: !!this.bookingFormV4.supportingDoc.ftzForm.taxNumber,
      pieces: this.bookingFormV4.pieces.map((piece) => ({
        pieceLength: piece.length || (this.isCipl ? 3 : 0),
        pieceWidth: piece.width || (this.isCipl ? 20 : 0),
        pieceHeight: piece.height || (this.isCipl ? 10 : 0),
        pieceGrossWeight: piece.grossWeight
      })),
      isDisablePromo: true,
      shipmentPrefix: "",
      isBookingPuposes: false
    });
  }

  fetchProductUpsellingDebounced = debounce(() => {
    this.fetchProductUpselling();
  }, 500);

  async fetchProductUpselling(): Promise<void> {
    const destination = this.destinationCityDetail;
    if (!destination?.id) return;

    this.productUpsellingLoading = true;
    this.productUpsellingIsError = false;
    this.errorRouteNotFound = false;

    try {
      const resp = await BookingV4Controller.postToGetPosterProduct(
        this.upsellingPayload
      );
      this.productUpsellingData = resp.map(
        (product: any) =>
          new PosterProduct({
            productType: product.name,
            estimateSla: product.sla,
            totalTariffAfterDiscount: product.total_tariff_after_discount,
            commissionRate: product.commission_rate,
            bookingCommission: product.booking_commission,
            isEmbargo: product.is_embargo,
            shippingCost: product.shipping_cost,
            isRecommended: Boolean(product.is_recommended),
            publishRate: product.publish_rate ?? 0,
            shippingSurchargeRate: product.shipping_surcharge_rate ?? 0,
            forwardRates: product.forward_rates ?? 0,
            totalTariff: product.total_tariff ?? 0
          })
      );
      if (!resp.length) return;

      // Autopopulate PRD §3.8 berdasarkan product type STT asal, fallback ke
      // opsi teratas bila mapping tidak tersedia pada rute tujuan.
      const sttProductType =
        this.sttProductTypeForReverseJourney ||
        this.sttDetail?.sttProductTypeName ||
        "";
      const productType = this.resolveDefaultProductForReverseJourney(
        sttProductType,
        this.productCardItems
      );
      if (productType) {
        this.bookingFormV4.product.productType = productType;
      }
    } catch (err) {
      this.handleErrorUpselling(err);
    } finally {
      this.productUpsellingLoading = false;

      const shouldRefetchEstimation = this.fetchPromisesAfterGetUpselling.includes(
        "getEstimationPriceDebounced"
      );
      this.fetchPromisesAfterGetUpselling = [];

      if (shouldRefetchEstimation) {
        this.getEstimationPriceDebounced();
      }
    }
  }

  handleErrorUpselling(err: any): void {
    this.productUpsellingData = [];
    this.productUpsellingIsError = err?.response?.status >= 500;
    this.errorRouteNotFound =
      !this.productUpsellingIsError &&
      err?.response?.data?.message?.id ===
        "Harga Shipping Surcharge dan Publish Rate untuk rute yang dipilih tidak tersedia";
    this.bookingFormV4.product.productType = "";
  }

  onSelectCurrentIndex(index: number) {
    setTimeout(() => {
      if (!validationCurrentIndex(index, this.currentIndex)) {
        this.currentIndex = index;
      }
    }, 0);
  }
}

export default ModalBookingReverseJourneyV4;
